import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Tooltip } from '../../../../components/Tooltip';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { Textarea } from '../../../../components/inputs/Textarea';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdMoney, TdNumber, TdSeamless, TdSelect } from '../../../../components/inline-add-table/cells';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { ApiError } from '../../../../lib/apiError';
import { formatMoney } from '../../../../lib/formatMoney';
import { itemIdentity } from '../../../../lib/itemIdentity';
import { focusValidationError } from '../../../../lib/validationFocus';
import { storageFacilitiesApi } from '../../../logistics/inventory/inventoryApi';
import type { StorageFacilityOption } from '../../../logistics/inventory/inventoryTypes';
import { purchaseRequestsApi } from '../purchaseRequestsApi';
import { PR_URGENCY, URGENCY_LABEL, URGENCY_OPTIONS, requiresJustification } from '../purchaseRequestTypes';
import type {
  PurchaseRequestDetail,
  PurchaseRequestItemOption,
  PurchaseRequestLineInput,
  PurchaseRequestUrgency,
} from '../purchaseRequestTypes';
import { samplePurchaseRequest } from '../utils/samplePurchaseRequest';

const ID_PREFIX = 'purchase-request';

/**
 * Raise or correct a purchase request.
 *
 * **NOT STEPPED.** One panel: the request's own facts above the lines, the way the SALN
 * editor puts a filing's facts above its schedules. A rail over two stages of work would be a
 * progress indicator for a journey the reader can already see all of.
 */
export function PurchaseRequestModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: PurchaseRequestDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [urgency, setUrgency] = useState<PurchaseRequestUrgency>(editing?.urgency ?? PR_URGENCY.normal);
  const [facilityId, setFacilityId] = useState<string | null>(
    editing?.deliverToStorageFacilityId ?? null,
  );
  const [justification, setJustification] = useState(editing?.justification ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');

  /*
    THE SALES QUOTATION, because the SOURCE'S OWN Add modal carries the section and the image
    the capture was given is the specification. It is NOT part of the create request: the
    endpoint is `POST /{id}/quotation` and needs the record to exist, so the file is filed in a
    SECOND call once the create returns — which is the repository dock's own one-file-per-request
    shape. An EDIT never offers it: the quotation is corrected from the profile's own card, where
    replacing one can say what it supersedes.
  */
  const [quotation, setQuotation] = useState<File | null>(null);

  const [rows, setRows] = useState<PurchaseRequestLineInput[]>(
    editing
      ? editing.items
          .filter((l) => l.isActive)
          .map((l) => ({
            inventoryItemId: l.inventoryItemId,
            requestedQuantity: l.requestedQuantity,
            /*
              `?? null` AT THE SEAM, because this is where a WIRE row becomes a FORM row and the
              two do not carry the same shape. The API is configured
              `DefaultIgnoreCondition = WhenWritingNull`, so an unpriced line sends no
              `estimatedUnitCost` at all and it arrives as `undefined` — while
              `PurchaseRequestLineInput` declares it `number | null`, which TypeScript then
              believes. Left un-normalised, `undefined * quantity` is NaN and the line total
              rendered `NaN` on every edit of an unpriced line.
              Normalising HERE is what makes the row type's promise true, so no comparison
              downstream — including one added later — has to know about the serializer.
            */
            estimatedUnitCost: l.estimatedUnitCost ?? null,
            justification: l.justification ?? null,
          }))
      : [],
  );

  /*
    NOT A FIELD OF THE RECORD. It says what the reader intends to do NEXT, so it sits below a
    rule rather than in the run of fields — and an EDIT never offers it: correcting a draft is
    not submitting it, and submitting has its own key.
  */
  const [submitNow, setSubmitNow] = useState(false);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const [items, setItems] = useState<PurchaseRequestItemOption[]>([]);
  const [facilities, setFacilities] = useState<StorageFacilityOption[]>([]);

  useEffect(() => {
    const controller = new AbortController();

    // Both pickers FAIL SOFT: a failed feed leaves the field empty and the rest of the form
    // working, rather than failing the whole dialog.
    purchaseRequestsApi
      .requestableItems(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setItems(next);
      })
      .catch(() => {});

    storageFacilitiesApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setFacilities(next);
      })
      .catch(() => {});

    return () => controller.abort();
  }, []);

  const update = useCallback((index: number, patch: Partial<PurchaseRequestLineInput>) => {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }, []);

  const itemsById = useMemo(
    () => new Map(items.map((i) => [i.id, i])),
    [items],
  );

  const filled = rows.filter((r) => r.inventoryItemId.length > 0 && r.requestedQuantity > 0);

  const duplicate = filled.some(
    (row, i) => filled.findIndex((other) => other.inventoryItemId === row.inventoryItemId) !== i,
  );

  const justificationNeeded = requiresJustification(urgency);
  const justificationMissing = justificationNeeded && justification.trim().length === 0;

  const valid = filled.length > 0 && filled.length === rows.length && !duplicate && !justificationMissing;
  const canSave = valid && confirmed && !saving;

  /*
    EVERY DISABLED CONTROL SAYS WHY, and it matters more now that the alert above is deferred:
    on a freshly opened form the blank row is silent, so the button is the only thing that can
    tell the reader what is still missing.
  */
  const saveDisabledReason = saving
    ? null
    : filled.length === 0
      ? 'Add an item and a quantity to the first line.'
      : filled.length !== rows.length
        ? 'Every line needs an item and a quantity. Remove the empty one, or fill it in.'
        : duplicate
          ? 'One item appears on two lines. Change the quantity on its own line instead.'
          : justificationMissing
            ? 'This urgency needs a justification.'
            : !confirmed
              ? 'Tick the confirm box to save.'
              : null;

  /*
    THE DRAFT'S OWN TOTAL, summed on screen. An unsaved request has no server figure to read,
    and the saved one is the server's — the panel reads that and this arithmetic is never
    consulted again. The SALN modal's own split.
  */
  const draftTotal = filled.reduce(
    (sum, row) => sum + (row.estimatedUnitCost ?? 0) * row.requestedQuantity,
    0,
  );

  // `!= null` as well as the seam above: two guards, because the next comparison added here
  // will be written by somebody who has not read the serializer's ignore condition.
  const anyPriced = filled.some((row) => row.estimatedUnitCost != null);

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const body = {
      urgency,
      deliverToStorageFacilityId: facilityId,
      justification: justification.trim() || null,
      notes: notes.trim() || null,
      items: rows,
    };

    try {
      if (editing) {
        await purchaseRequestsApi.update(editing.id, body);
      } else {
        const created = await purchaseRequestsApi.create({ ...body, submitImmediately: submitNow });

        /*
          THE REQUEST IS WRITTEN FIRST and the file follows, because two calls share no
          transaction and this is the harmless direction to fail in: a request with no quotation
          is recoverable from its own profile card, where a quotation with no request would be a
          document filed against nothing. The failure is REPORTED rather than swallowed — the
          reader chose a file, so silence would leave them believing it was filed.
        */
        if (quotation !== null) {
          await purchaseRequestsApi.attachQuotation(created.id, quotation);
        }
      }

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this request.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID_PREFIX });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = samplePurchaseRequest(items, facilities);

    /*
      THE URGENCY IS LEFT ALONE. It reveals and requires the justification field, so a
      generator that moved it would produce a different form on every click — the rule that
      keeps a step-adding flag at its default.
    */
    setFacilityId(sample.facilityId);
    setNotes(sample.notes);
    setRows(sample.rows);

    // Filled ANYWAY, whatever the urgency says, so one click leaves a submittable form.
    setJustification(sample.justification);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.requestNumber}` : 'New purchase request'}
      size="3xl"
      id={`${ID_PREFIX}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{
        id: `${ID_PREFIX}-confirm`,
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={saveDisabledReason}>
            <Button
              id={`${ID_PREFIX}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id={`${ID_PREFIX}-urgency`}
            label="Urgency"
            required
            description="Critical and Emergency jump the ordinary procurement cadence, so both have to say why. The approver sees that word before they open anything."
            value={String(urgency)}
            onChange={(next) => setUrgency(Number(next) as PurchaseRequestUrgency)}
            options={URGENCY_OPTIONS}
            clearable={false}
            sort="none"
            error={error?.fieldError('urgency')}
            disabled={saving}
          />

          <Select
            id={`${ID_PREFIX}-deliver-to-storage-facility-id`}
            label="Deliver to"
            description="Where the stock should land. Optional — a request can be raised before that is settled."
            value={facilityId}
            onChange={setFacilityId}
            options={facilities.map((f) => ({ value: f.id, label: f.name, sublabel: f.code }))}
            remote
            error={error?.fieldError('deliverToStorageFacilityId')}
            disabled={saving}
          />
        </div>

        <InlineAddTable
          headers={[
            'Item *',
            { label: 'Qty *', align: 'right' },
            { label: 'Est. unit cost', align: 'right' },
            { label: 'Line total', align: 'right' },
            'Justification',
          ]}
          /*
            NO `flexColumn`. Measured at 999,242px on the Justification column: the flexing
            branch asks it for `width: 100%` under a table carrying `min-width: max-content`,
            which is circular. It read correctly on screen only because the blown column is LAST
            and its content is short — which is the shape of defect a screenshot cannot show.

            **RE-MEASURED 29 Sep 2026 AND IT STILL BLOWS OUT — 999,242px, the same figure.**
            `flexColumn={4}` was added that day on the strength of `<InlineAddTable>`'s own
            comment, which records the flex branch being FIXED by giving it a
            `min-width: max-content` floor, and was reverted within the hour when the rendered
            header was read back. The floor fixes a DIFFERENT failure — a dragged width being
            squeezed back — and does nothing about the circularity. Do not re-add it on the
            strength of that comment; measure the header first.
          */
          colWidths={[300, 90, 140, 140, 240]}
          resizeKey={`${ID_PREFIX}-lines-v1`}
          rowCount={rows.length}
          onAddRow={() =>
            setRows((current) => [
              ...current,
              { inventoryItemId: '', requestedQuantity: 1, estimatedUnitCost: null, justification: null },
            ])
          }
          addDisabled={saving}
          addButtonId={`${ID_PREFIX}-add-line`}
          footerContent={
            /*
              SHORT, because the footer sits inside the grid's own horizontal scroll region and
              a long sentence is CLIPPED rather than wrapped — which is how the first version
              read "…null means nobody h" on screen. The two facts worth keeping are the ones a
              reader can act on; the reasoning behind them is in the code.
            */
            <div className="flex items-center justify-between gap-4">
              <p className="text-xs text-slate-600">One line per item. A line with no cost is fine.</p>

              <p className="shrink-0 font-mono text-xs tabular-nums text-slate-600">
                {anyPriced ? `Est. total ${formatMoney(draftTotal)}` : 'Est. total —'}
              </p>
            </div>
          }
        >
          {rows.map((row, index) => {
            const lineTotal =
              row.estimatedUnitCost == null ? null : row.estimatedUnitCost * row.requestedQuantity;

            return (
              /*
                `<InlineRow>`, not a hand-rolled `<tr>`. It owns the `#` gutter AND the trailing
                remove cell, so the data cells here line up ONE-FOR-ONE with `headers` and
                `colWidths`.

                Hand-rolled, the row carried two extra cells the widths knew nothing about and
                the whole grid shifted left — the item column squeezed to a single character
                while the headers stayed put. It type-checked and linted clean; only looking at
                the screen found it.
              */
              <InlineRow key={index} idx={index + 1} onRemove={() => setRows((current) => current.filter((_, i) => i !== index))}>
                <TdSelect
                  id={`${ID_PREFIX}-items.${index}.inventoryItemId`}
                  ariaLabel={`Item on line ${index + 1}`}
                  value={row.inventoryItemId || null}
                  onChange={(next) => {
                    const picked = itemsById.get(next);

                    update(index, {
                      inventoryItemId: next,
                      /*
                        THE LAST PRICED LOT seeds the cost, and only over an empty cell — a
                        reader who has typed a figure keeps it. The catalog carries no unit
                        cost of its own, so this is the honest source for a suggestion.
                      */
                      estimatedUnitCost: row.estimatedUnitCost ?? picked?.lastUnitCost ?? null,
                    });
                  }}
                  /*
                    THE LABEL IS THE PRODUCT — product call, 29 Sep 2026, in the reader's own
                    words: *display the actual brand/model. nobody gives a fuck on the sku and
                    category.*

                    It was `name` over `sku · category`, and `name` is the item TYPE's — so a
                    catalog holding three laptops offered "Laptop" three times and the reader
                    could only tell them apart by a SKU nobody has memorised. The identity line
                    is the Stock Levels anchor's own, through `lib/itemIdentity`, so the picker
                    and the table it is chosen off cannot write one product two ways.

                    THE SUBLABEL STATES THE TYPE, and only where the label is NOT already it —
                    printing the same string twice is what `<ProfileIdentity>` suppresses one
                    surface along. The UNIT stays either way: the cell beside this one asks for
                    a quantity, and `1` of something means nothing without it.
                  */
                  options={items.map((i) => {
                    const identity = itemIdentity(i);

                    return {
                      value: i.id,
                      label: identity || i.name,
                      sublabel: identity ? `${i.name} · ${i.unit}` : i.unit,
                      /*
                        THE SKU STAYS SEARCHABLE. It left the LABEL because nobody reads it;
                        a reader who knows it still finds the row, which is the rule the
                        plantilla picker already follows — what is displayed narrows, what is
                        searchable must not.
                      */
                      keywords: [i.sku, i.categoryName].filter(Boolean).join(' '),
                    };
                  })}
                  remote
                  disabled={saving}
                />

                <TdNumber
                  id={`${ID_PREFIX}-items.${index}.requestedQuantity`}
                  ariaLabel={`Quantity on line ${index + 1}`}
                  value={row.requestedQuantity}
                  onChange={(next) => update(index, { requestedQuantity: next ?? 0 })}
                  integerOnly
                  min={1}
                  disabled={saving}
                />

                <TdMoney
                  id={`${ID_PREFIX}-items.${index}.estimatedUnitCost`}
                  ariaLabel={`Estimated unit cost on line ${index + 1}`}
                  value={row.estimatedUnitCost}
                  onChange={(next) => update(index, { estimatedUnitCost: next })}
                  disabled={saving}
                />

                {/*
                  READ-ONLY, because it is quantity times cost and nothing else. An editable
                  cell here would be a third answer free to disagree with the two beside it.
                */}
                <TdSeamless align="right">
                  <span className="font-mono text-xs tabular-nums text-slate-600">
                    {lineTotal === null ? '—' : formatMoney(lineTotal)}
                  </span>
                </TdSeamless>

                <TdSeamless>
                  <input
                    id={`${ID_PREFIX}-items.${index}.justification`}
                    aria-label={`Justification on line ${index + 1}`}
                    className="w-full border-0 bg-transparent px-2 py-1 text-xs text-slate-600 focus:outline-none"
                    value={row.justification ?? ''}
                    onChange={(e) => update(index, { justification: e.target.value || null })}
                    placeholder="optional"
                    disabled={saving}
                  />
                </TdSeamless>
              </InlineRow>
            );
          })}
        </InlineAddTable>

        {/*
          THE AUTO-INSERTED ROW IS NOT A MISTAKE THE READER MADE, so this does not fire until
          they have filled something in — `<InlineAddTable>` seeds a blank row on open, and an
          alert keyed on `filled.length !== rows.length` alone turned every freshly opened form
          red. "A form never opens in a failed state", reaching a second call site of one shared
          component. The Save button's own reason still states it from the first paint.

          The `rows.length === 0` branch went with it: `addDisabled` is only ever `saving`, so
          the table re-inserts a blank the moment the last row is removed and the count cannot
          rest at zero.
        */}
        {filled.length > 0 && filled.length !== rows.length ? (
          <p role="alert" className="text-xs text-red-700">
            Every line needs an item and a quantity. Remove the empty one, or fill it in.
          </p>
        ) : null}

        {duplicate ? (
          <p role="alert" className="text-xs text-red-700">
            One item appears on two lines. Change the quantity on its own line instead.
          </p>
        ) : null}

        {/*
          IT READS DIRECTLY ABOVE NOTES — product call, 05 Oct 2026. It sat above the line grid,
          beside the urgency that decides whether it is required; the two free-text boxes now
          read together after the lines, which is also where Notes' own description points
          ("anything … that is not a justification").

          THE COST IS STATED RATHER THAN HIDDEN: a reader who picks Critical or Emergency no
          longer sees the field that choice just made required, because the grid is between
          them. The Save button's own disabled reason still names it from the first paint.

          FULL WIDTH, outside the grid — the remarks rule. It is the longest value on the form
          and the one most likely to be a sentence, so half a row is the box that truncates
          while the field beside it needed none of the room.

          DRAWN ALWAYS, never revealed by the urgency: a field that appeared and vanished as the
          urgency changed would move every field under it while the reader worked. What changes
          is whether it is REQUIRED.
        */}
        <Textarea
          id={`${ID_PREFIX}-justification`}
          label="Justification"
          required={justificationNeeded}
          description={
            justificationNeeded
              ? 'Required at this urgency. Name the shortage or the deadline — this is what the approver reads to decide whether the cadence should be jumped.'
              : 'Why this is needed now. Optional at Normal and Urgent.'
          }
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          error={
            justificationMissing
              ? `${URGENCY_LABEL[urgency]} requests have to say why the ordinary cadence is being jumped.`
              : error?.fieldError('justification')
          }
          disabled={saving}
        />

        <Textarea
          id={`${ID_PREFIX}-notes`}
          label="Notes"
          description="A free note about the request as a whole — anything the approver or the buyer should know that is not a justification."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          error={error?.fieldError('notes')}
          disabled={saving}
        />

        {/*
          THE SALES QUOTATION section, in the source's own position: after NOTES and before the
          skip-draft tick. Only on a CREATE — an edit corrects it from the profile card.
        */}
        {editing === null ? (
          <PermissionGate permission="purchase-requests.update">
            <div>
              <label
                htmlFor={`${ID_PREFIX}-quotation`}
                className="mb-1.5 block text-sm font-medium text-slate-700"
              >
                Sales quotation
              </label>

              <input
                id={`${ID_PREFIX}-quotation`}
                name={`${ID_PREFIX}-quotation`}
                type="file"
                accept="application/pdf,.pdf,image/png,.png,image/jpeg,.jpg,.jpeg"
                disabled={saving}
                onChange={(event) => setQuotation(event.target.files?.[0] ?? null)}
                className="block w-full rounded-lg border border-slate-300 bg-white text-sm text-slate-600 file:mr-3 file:cursor-pointer file:rounded-l-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:text-slate-700 hover:file:bg-slate-200 disabled:cursor-not-allowed disabled:bg-slate-50"
              />

              {/*
                OPTIONAL, and the help text says what happens WITHOUT one rather than leaving the
                reader to infer that an empty file input means "none yet".
              */}
              <p className="mt-1.5 text-xs text-slate-500">
                Optional. A supplier's quotation, filed with the request — PDF, PNG or JPEG up to
                10 MB. Without one the request is raised on its estimates, and the quotation can
                be filed from its profile when it arrives.
              </p>
            </div>
          </PermissionGate>
        ) : null}

        {/*
          BELOW A RULE, and only on a CREATE. It says what happens NEXT rather than recording a
          fact about the request, and an edit never offers it — correcting a draft is not
          submitting it. Gated on the submit key, because that is the act it performs.
        */}
        {editing === null ? (
          <PermissionGate permission="purchase-requests.submit">
            <div className="border-t border-slate-200 pt-4">
              <label
                htmlFor={`${ID_PREFIX}-submit-now`}
                className="flex items-start gap-2 text-sm text-slate-600"
              >
                <Checkbox
                  id={`${ID_PREFIX}-submit-now`}
                  name={`${ID_PREFIX}-submit-now`}
                  checked={submitNow}
                  onChange={() => setSubmitNow((v) => !v)}
                  disabled={saving}
                />
                <span>
                  {/* THE IMAGE'S OWN WORDS. It read "Submit for approval immediately", which was
                      this capture's invention — the source's tick says Skip draft. */}
                  Skip draft
                  <span className="block text-xs text-slate-500">
                    The request is routed straight into the approval cycle instead of being held
                    as a draft. Both happen in ONE transaction, so it cannot end up submitted
                    with no approval behind it — or approved with no request.
                  </span>
                </span>
              </label>
            </div>
          </PermissionGate>
        ) : null}
      </div>
    </Modal>
  );
}
